import React, { useState, useEffect } from 'react'
import Spacer from '@berlitz/spacer'
import {
  CardWrapper,
  ScromContainer,
  StyledCollapseWrapper,
  ModalContent,
  ModalTitle,
  SelectContainer,
  StyledSelect,
  SelectChevron,
  ModalDescription,
  ModalFooter,
  CancelButton,
  OkButton,
  AiTitleGroup,
  AiBadge,
  NoContentMessage,
  ModalNote,
  ModalWrapper,
} from './style'
import Collapse from '@components/Collapse'
import { TileRow } from '@layouts/LearningPath/Layout/style'
import { langToLocaleMap } from '../helpers'
import Modal from '@berlitzplatforms/micro.ui.modal'
import { FormattedMessage } from 'react-intl'

const ScormPanel = ({ scormUnits, renderUnitCard, hasContent, languageOptions, currentLanguage, onLanguageChange }: any) => {
  const [pendingLang, setPendingLang] = useState(currentLanguage || languageOptions[0]?.value || '')
  const [openModal, setOpenModal] = useState(!currentLanguage)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)

  useEffect(() => {
    if (languageOptions.length === 1 && languageOptions[0].value) {
      const lang = languageOptions[0].value
      localStorage.setItem('pdLanguage', lang)
      onLanguageChange(lang)
    }
  }, [languageOptions, onLanguageChange])

  useEffect(() => {
    if (currentLanguage && languageOptions.length > 0) {
      const isValid = languageOptions.some((opt: any) => opt.value === currentLanguage)
      if (!isValid) {
        localStorage.removeItem('pdLanguage')
        setPendingLang(languageOptions[0]?.value);
        onLanguageChange('')
        setOpenModal(true)
      }
    }
  }, [languageOptions, currentLanguage, onLanguageChange])

  const handleOpenModal = () => {
    setPendingLang(currentLanguage || languageOptions[0]?.value || '')
    setOpenModal(true)
  }

  const handleConfirm = () => {
    localStorage.setItem('pdLanguage', langToLocaleMap[pendingLang])
    onLanguageChange(pendingLang)
    setOpenConfirmModal(false)
  }

  const handleOkClick = () => {
    if (!pendingLang) return
    if (!currentLanguage) {
      localStorage.setItem('pdLanguage', langToLocaleMap[pendingLang])
      onLanguageChange(pendingLang)
      setOpenModal(false)
      return
    }
    setOpenModal(false)
    setOpenConfirmModal(true)
  }

  return (
    <>
      <br />
      <Collapse activeIds={['professional-development']}>
        <Collapse.Panel
          id="professional-development"
          header={
            <StyledCollapseWrapper>
              <AiTitleGroup>
                <span>
                  <FormattedMessage
                    id="AI Literacy & Compliance Training"
                    defaultMessage={'AI Literacy & Compliance Training'}
                  />
                </span>
                <AiBadge
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenModal()
                  }}
                >
                  <FormattedMessage id="Change language" defaultMessage={'Change language'} />
                </AiBadge>
              </AiTitleGroup>
            </StyledCollapseWrapper>
          }
        >
          {/* <AILiteracyProgress units={scormUnits} /> */}
          {currentLanguage ? (
            hasContent ? (
              <TileRow id="softskills-scroller">
                <ScromContainer>
                  {scormUnits.map((unit: any, index: number) => (
                    <CardWrapper key={unit.unitid || index} id={`tile-${unit.unitid}-softskills`}>
                      {renderUnitCard(unit)}
                    </CardWrapper>
                  ))}
                </ScromContainer>
              </TileRow>
            ) : (
              <NoContentMessage>
                <FormattedMessage
                  id="Please select a language for your Professional Development Courses"
                  defaultMessage={'Please select a language for your Professional Development Courses'}
                  values={{ br: '' }}
                />
              </NoContentMessage>
            )
          ) : (
            <NoContentMessage>
              <FormattedMessage
                id="Please select a language for your Professional Development Courses"
                defaultMessage={'Please select a language for your Professional Development Courses'}
                values={{ br: '' }}
              />
            </NoContentMessage>
          )}
        </Collapse.Panel>
      </Collapse>

      <ModalWrapper hideClose={!currentLanguage}>
        <Modal open={openModal} onClose={!currentLanguage ? () => {} : () => setOpenModal(false)}>
          <ModalContent>
          <ModalTitle>
            {!currentLanguage ? (
              <FormattedMessage
                id="Please select a language for your Professional Development Courses"
                defaultMessage={'Please select a language for your Professional Development Courses'}
                values={{ br: <br /> }}
              />
            ) : (
              <FormattedMessage
                id="Select a different language for your Professional Development courses"
                defaultMessage={'Select a different language for your Professional Development courses'}
                values={{ br: <br /> }}
              />
            )}
          </ModalTitle>
          {currentLanguage && (
            <ModalDescription>
              <FormattedMessage
                id="Important: You will lose your current progress if you change the default language for your Professional Development courses"
                defaultMessage={
                  '<strong>Important: You will lose your current progress if you change the default language for your Professional Development courses</strong>'
                }
                values={{
                  strong: (chunks) => <strong id="strong-text">{chunks}</strong>,
                }}
              />
            </ModalDescription>
          )}
          <SelectContainer>
            <StyledSelect value={pendingLang} onChange={(e) => setPendingLang(e.target.value)}>
              {languageOptions.map((opt: any) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </StyledSelect>
            <SelectChevron />
          </SelectContainer>
          {!currentLanguage && (
            <ModalNote>
              <FormattedMessage
                id="Please note: Changing your language in the future may cause you to lose your progress."
                defaultMessage={
                  'Please note: Changing your language in the future may cause you to lose your progress.'
                }
                values={{
                  strong: (chunks) => <strong id="strong-text">{chunks}</strong>,
                }}
              />
            </ModalNote>
          )}
          <ModalFooter>
            {currentLanguage && (
              <CancelButton onClick={() => setOpenModal(false)}>
                <FormattedMessage id="Cancel" defaultMessage={'Cancel'} />
              </CancelButton>
            )}
            <OkButton onClick={handleOkClick}>
              <FormattedMessage id="Ok" defaultMessage={'Ok'} />
            </OkButton>
          </ModalFooter>
          </ModalContent>
        </Modal>
      </ModalWrapper>

      <Modal open={openConfirmModal} onClose={() => setOpenConfirmModal(false)}>
        <ModalContent>
          <ModalTitle>
            <FormattedMessage
              id="Are you sure you want to change your language?"
              defaultMessage={'Are you sure you want to change your language?'}
            />
            <br />
            <FormattedMessage
              id="You will lose your course progress"
              defaultMessage={'You will lose your course progress'}
            />
          </ModalTitle>
          <ModalFooter>
            <CancelButton onClick={() => setOpenConfirmModal(false)}>
              <FormattedMessage id="Cancel" defaultMessage={'Cancel'} />
            </CancelButton>
            <OkButton onClick={handleConfirm}>
              <FormattedMessage id="Ok" defaultMessage={'Ok'} />
            </OkButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Spacer size="xxxl" />
    </>
  )
}

export default ScormPanel
